<template>
  <div class="stripe-checkout">
    <div class="payment-methods-box express-box">
      <div id="express-checkout-element"></div>
    </div>
    <div class="payment-methods-box card-box">
      <div id="payment-element"></div>
    </div>
    <div id="card-errors" class="error-message" role="alert" v-if="error">
      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="me-1"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
      {{ error }}
    </div>
    <button
      class="btn-pay-now-premium w-100"
      :disabled="loading"
      @click="handlePay"
    >
      <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
      {{ loading ? 'Elaborazione...' : payButtonLabel }}
    </button>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, computed } from 'vue'
import { loadStripe } from '@stripe/stripe-js'

const props = defineProps({
  publishableKey: {
    type: String,
    required: true
  },
  clientSecret: {
    type: String,
    required: true
  },
  orderId: {
    type: [String, Number],
    required: true
  },
  totalAmount: {
    type: Number,
    required: true
  },
  merchantName: {
    type: String,
    default: 'Billy B'
  },
  email: {
    type: String,
    default: ''
  },
  payButtonLabel: {
    type: String,
    default: 'Paga'
  }
})

const emit = defineEmits(['success', 'error', 'cancel'])

const stripe = ref(null)
const checkout = ref(null)
const loading = ref(false)
const error = ref('')
const actions = ref(null)

let expressCheckoutElement = null
let paymentElement = null

onMounted(async () => {
  try {
    stripe.value = await loadStripe(props.publishableKey)
    if (!stripe.value) {
      throw new Error('Impossibile caricare Stripe.')
    }

    checkout.value = await stripe.value.initCheckoutElementsSdk({
      clientSecret: props.clientSecret
    })

    const loadActionsResult = await checkout.value.loadActions()
    if (loadActionsResult.type === 'success') {
      actions.value = loadActionsResult.actions
    }

    expressCheckoutElement = checkout.value.createExpressCheckoutElement({
      buttonType: {
        applePay: 'buy',
        googlePay: 'buy',
        paypal: 'buynow',
        klarna: 'pay'
      },
      buttonTheme: {
        applePay: 'white-outline',
        googlePay: 'black'
      },
      paymentMethods: {
        applePay: 'always',
        googlePay: 'always',
        /* paypal / klarna / link: 'always' NON è valido -> usano 'auto' (default).
           'always' è ammesso SOLO per applePay/googlePay. PayPal e Klarna compaiono
           solo se attivati in Dashboard e supportati dal browser/paese/valuta. */
        paypal: 'auto',
        klarna: 'auto',
        link: 'auto'
      }
    })
    expressCheckoutElement.mount('#express-checkout-element')

    expressCheckoutElement.on('availablepaymentmethodschange', ({ paymentMethods }) => {
      // payment methods changed
    })

    expressCheckoutElement.on('cancel', () => {
      loading.value = false
      error.value = ''
      emit('cancel')
    })

    expressCheckoutElement.on('confirm', async (event) => {
      if (loading.value) return
      loading.value = true
      error.value = ''
      try {
        if (!actions.value) {
          throw new Error('Azioni di pagamento non disponibili. Riprova.')
        }
        const confirmOptions = props.email ? { email: props.email } : {}
        const result = await actions.value.confirm({ expressCheckoutConfirmEvent: event, ...confirmOptions })
        handleResult(result)
      } catch (e) {
        handleResult({ type: 'error', error: { message: e.message || 'Errore durante il pagamento.' } })
      } finally {
        loading.value = false
      }
    })

    paymentElement = checkout.value.createPaymentElement({
      layout: 'accordion',
      wallets: {
        googlePay: 'auto',
        applePay: 'auto'
      }
    })
    paymentElement.mount('#payment-element')
  } catch (e) {
    const message = 'Errore caricamento modulo di pagamento.'
    error.value = message
    emit('error', e)
    console.error('[StripeCheckout]', e)
  }
})

const handlePay = async () => {
  if (!actions.value || loading.value) return
  loading.value = true
  error.value = ''
  try {
    const confirmOptions = props.email ? { email: props.email } : {}
    const result = await actions.value.confirm(confirmOptions)
    handleResult(result)
  } catch (e) {
    handleResult({ type: 'error', error: { message: e.message || 'Errore durante il pagamento.' } })
  } finally {
    loading.value = false
  }
}

const handleResult = (result) => {
  if (result.type === 'success') {
    const paymentIntent = result.paymentIntent
    if (paymentIntent && paymentIntent.status === 'succeeded') {
      emit('success', { orderId: props.orderId })
    } else if (paymentIntent && (paymentIntent.status === 'processing' || paymentIntent.status === 'requires_confirmation' || paymentIntent.status === 'requires_action')) {
      error.value = 'Pagamento in elaborazione. Attendi la conferma.'
      emit('error', { message: 'Pagamento in elaborazione. Attendi la conferma.' })
    } else if (paymentIntent) {
      console.warn('[StripeCheckout] Unexpected payment intent status:', paymentIntent.status)
      error.value = 'Pagamento non completato. Riprova.'
      emit('error', { message: 'Pagamento non completato.' })
    } else {
      emit('success', { orderId: props.orderId })
    }
  } else if (result.type === 'error') {
    error.value = result.error?.message || 'Errore durante il pagamento.'
    emit('error', result.error)
  }
}

onBeforeUnmount(() => {
  expressCheckoutElement?.unmount()
  paymentElement?.unmount()
})
</script>

<style scoped>
.stripe-checkout {
  font-family: 'Inter', Arial, sans-serif;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.payment-methods-box {
  width: 100%;
  box-sizing: border-box;
  background: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 16px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.payment-methods-box:focus-within {
  border-color: #000000;
  box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.05);
}

#express-checkout-element,
#payment-element {
  width: 100%;
  box-sizing: border-box;
  min-height: 40px;
}

.error-message {
  background: #fff5f5;
  border: 1px solid #fed7d7;
  border-left: 3px solid #fa755a;
  padding: 12px 14px;
  border-radius: 4px;
  box-sizing: border-box;
  width: 100%;
  font-family: 'Inter', Arial, sans-serif;
  font-size: 12px;
  color: #fa755a;
  display: flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 2px 4px rgba(250, 117, 90, 0.1);
}

.btn-pay-now-premium {
  font-family: 'Inter', Arial, sans-serif;
  width: 100%;
  box-sizing: border-box;
  background: #000000;
  color: #ffffff;
  border: none;
  padding: 18px;
  font-size: 12px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 2px;
  transition: all 0.3s ease;
  border-radius: 6px;
}

.btn-pay-now-premium:hover:not(:disabled) {
  background: #222222;
  transform: translateY(-1px);
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.15);
}

.btn-pay-now-premium:disabled {
  background: #e0e0e0;
  color: #a0a0a0;
  cursor: not-allowed;
  box-shadow: none;
  transform: none;
}
</style>
